import React from 'react';
import { motion } from 'framer-motion';
import { XCircle } from 'lucide-react';
export function ProblemSection() {
  const treatments = [{
    name: 'Alpha-blockers',
    subtitle: '(Flomax, etc)',
    image: 'https://cdn.magicpatterns.com/uploads/kxXkZJc9r37ys9rnq1UiT2/6.svg',
    whatItDoes: 'Relax prostate and bladder muscles',
    whyItFails: 'Do nothing for the external vein pressure crushing the bladder.'
  }, {
    name: '5-ARIs',
    subtitle: '(Finasteride)',
    image: 'https://cdn.magicpatterns.com/uploads/1s8FKptUM68LnJ8Q3jT7zr/8.svg',
    whatItDoes: 'Shrink prostate tissue over months',
    whyItFails: 'Bladder still gets squeezed from outside; often comes with sexual side effects.'
  }, {
    name: 'Saw Palmetto',
    subtitle: '& similar herbs',
    image: 'https://cdn.magicpatterns.com/uploads/9Y5kSicVvhCHucEWBVUdWr/7.svg',
    whatItDoes: 'Supports prostate cells',
    whyItFails: 'Does not address circulation at all; JAMA study found it no better than placebo.'
  }];
  return <section className="py-20 bg-[#020617]" data-id="element-197">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-198">
        <div className="mb-16" data-id="element-199">
          <h3 className="text-2xl font-bold text-center text-white mb-8" data-id="element-200">
            This is why prostate pills do not work for most men:
          </h3>

          {/* Mobile Card Layout */}
          <div className="md:hidden space-y-4" data-id="element-201">
            {treatments.map((treatment, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className="bg-slate-900/50 border border-slate-700 rounded-xl p-4 space-y-3" data-id="element-202">
                <div className="flex items-center gap-3" data-id="element-203">
                  <img src={treatment.image} alt={treatment.name} className="w-14 h-14 object-contain rounded-lg border border-slate-700" data-id="element-204" />
                  <div data-id="element-205">
                    <div className="font-bold text-white text-base" data-id="element-206">
                      {treatment.name}
                    </div>
                    <div className="text-sm text-slate-400" data-id="element-207">
                      {treatment.subtitle}
                    </div>
                  </div>
                </div>

                <div className="space-y-2 text-sm" data-id="element-208">
                  <div data-id="element-209">
                    <div className="text-slate-500 font-semibold mb-1" data-id="element-210">
                      What It Does:
                    </div>
                    <div className="text-slate-300" data-id="element-211">{treatment.whatItDoes}</div>
                  </div>

                  <div data-id="element-212">
                    <div className="text-slate-500 font-semibold mb-1 flex items-center gap-1" data-id="element-213">
                      <XCircle className="w-4 h-4 text-red-400" data-id="element-214" />
                      Why It Fails:
                    </div>
                    <div className="text-slate-300 pl-5" data-id="element-215">
                      {treatment.whyItFails}
                    </div>
                  </div>
                </div>
              </motion.div>)}
          </div>

          {/* Desktop Table Layout */}
          <div className="hidden md:block overflow-hidden rounded-xl border border-slate-700 shadow-sm" data-id="element-216">
            <table className="w-full text-left border-collapse" data-id="element-217">
              <thead data-id="element-218">
                <tr className="bg-slate-800 border-b border-slate-700" data-id="element-219">
                  <th className="p-6 text-lg font-bold text-white uppercase tracking-wider w-1/3" data-id="element-220">
                    What You've Tried
                  </th>
                  <th className="p-6 text-lg font-bold text-white uppercase tracking-wider w-1/3" data-id="element-221">
                    What It Does
                  </th>
                  <th className="p-6 text-lg font-bold text-white uppercase tracking-wider" data-id="element-222">
                    Why It Fails
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700 bg-slate-900/50" data-id="element-223">
                {treatments.map((treatment, idx) => <tr key={idx} data-id="element-224">
                    <td className="p-6" data-id="element-225">
                      <div className="flex items-center gap-3" data-id="element-226">
                        <img src={treatment.image} alt={treatment.name} className="w-16 h-16 object-contain rounded-lg border border-slate-700" data-id="element-227" />
                        <div data-id="element-228">
                          <div className="font-bold text-white text-lg" data-id="element-229">
                            {treatment.name}
                          </div>
                          <div className="text-base text-slate-400" data-id="element-230">
                            {treatment.subtitle}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="p-6 text-slate-300 text-lg" data-id="element-231">
                      {treatment.whatItDoes}
                    </td>
                    <td className="p-6" data-id="element-232">
                      <div className="flex items-start space-x-2" data-id="element-233">
                        <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-234" />
                        <span className="text-slate-300 text-lg" data-id="element-235">
                          {treatment.whyItFails}
                        </span>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          <div className="text-center mt-8" data-id="element-236">
            <p className="text-xl font-medium text-blue-400" data-id="element-237">
              The problem was never just your prostate.{' '}
              <br className="hidden md:block" data-id="element-238" />
              It was the{' '}
              <span className="font-bold underline decoration-emerald-400 decoration-4 underline-offset-2" data-id="element-239">
                circulation collapse
              </span>{' '}
              underneath it.
            </p>
          </div>
        </div>
      </div>
    </section>;
}